/**
 * Brand-styled Recharts radar — used in the assessment wizard live preview,
 * the run detail view, and the compare view.
 *
 * Props:
 *   - series: [{ name, dimensions: [{dim, score}], colour }]
 *   - benchmark: { name, label, sampleSize, dimensions: [{dim, low, median, high}] } | null
 *   - max: max scale value (default 5)
 *   - height: chart height
 */
import React from "react";
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  Radar, ResponsiveContainer, Legend, Tooltip,
} from "recharts";
import { BRAND, COMPARE_PALETTE } from "../../data/brandColors";

/**
 * Wrap a label into ≤2 lines, balancing them around the midpoint at a word
 * boundary. Single-word labels stay on one line.
 */
function wrapLabel(label, maxChars = 16) {
  if (!label) return [""];
  const words = label.split(/\s+/);
  if (words.length === 1) return [label];
  if (label.length <= maxChars) return [label];
  // Try every word-boundary split; pick the one whose two halves are most
  // balanced (minimum max-line-length).
  let best = { score: Infinity, lines: [label] };
  for (let i = 1; i < words.length; i++) {
    const a = words.slice(0, i).join(" ");
    const b = words.slice(i).join(" ");
    const score = Math.max(a.length, b.length);
    if (score < best.score) best = { score, lines: [a, b] };
  }
  return best.lines;
}

/**
 * Custom angle-axis tick that:
 *   • Anchors text based on the tick's x position (left/right/centre).
 *   • Wraps long labels onto two lines using <tspan>.
 *   • Nudges the label outward so it never overlaps the radar polygon.
 */
function AngleTick({ x, y, cx, cy, payload, textAnchor }) {
  const label = payload?.value || "";
  const lines = wrapLabel(label, 18);

  // Prefer recharts-computed dx/dy when cx/cy are present; fall back to textAnchor.
  const hasCenter = Number.isFinite(cx) && Number.isFinite(cy);
  const dx = hasCenter ? x - cx : 0;
  const dy = hasCenter ? y - cy : 0;
  let anchor = textAnchor || "middle";
  if (hasCenter) {
    if (dx > 8) anchor = "start";
    else if (dx < -8) anchor = "end";
    else anchor = "middle";
  }

  // Push label radially outward a touch so it clears the polygon stroke.
  const radius = Math.sqrt(dx * dx + dy * dy) || 1;
  const offset = 6;
  const ox = hasCenter ? x + (dx / radius) * offset : x;
  const oy = hasCenter ? y + (dy / radius) * offset : y;

  // Vertical alignment: top labels need to sit above the point, bottom below.
  let baseDy = 4;
  if (hasCenter) {
    if (dy < -8) baseDy = -4;
    else if (dy > 8) baseDy = 12;
    else baseDy = 4;
  }

  const lineHeight = 13;

  return (
    <text
      x={ox}
      y={oy}
      textAnchor={anchor}
      fill={BRAND.ink}
      fontSize={11}
      fontWeight={500}
    >
      {lines.map((ln, i) => (
        <tspan key={i} x={ox} dy={i === 0 ? baseDy : lineHeight}>
          {ln}
        </tspan>
      ))}
    </text>
  );
}

export default function RadarMaturity({ series = [], benchmark = null, max = 5, height = 360 }) {
  if (!series.length) return null;
  const dimensions = series[0].dimensions || [];
  const benchmarkLookup = benchmark
    ? Object.fromEntries(benchmark.dimensions.map((d) => [d.dim, d]))
    : null;

  const data = dimensions.map((d) => {
    const row = { dim: d.dim };
    series.forEach((s) => {
      const found = s.dimensions.find((x) => x.dim === d.dim);
      row[s.name] = found ? Math.round(found.score * 100) / 100 : 0;
    });
    if (benchmarkLookup) {
      const b = benchmarkLookup[d.dim];
      if (b) {
        row.__band_low = b.low;
        row.__band_high = b.high;
        row.__band_median = b.median;
      }
    }
    return row;
  });

  return (
    <div className="w-full" data-testid="radar-maturity">
      <ResponsiveContainer width="100%" height={height}>
        <RadarChart
          cx="50%"
          cy="50%"
          outerRadius="64%"
          data={data}
          margin={{ top: 24, right: 80, bottom: 32, left: 80 }}
        >
          <PolarGrid stroke="#d8d8df" />
          <PolarAngleAxis
            dataKey="dim"
            tick={(props) => <AngleTick {...props} />}
          />
          <PolarRadiusAxis
            angle={90}
            domain={[0, max]}
            tickCount={max + 1}
            stroke="#bbb"
            tick={{ fill: "#777", fontSize: 10 }}
          />

          {benchmarkLookup ? (
            <>
              <Radar
                name={`${benchmark.name} · upper`}
                dataKey="__band_high"
                stroke="transparent"
                fill={BRAND.purpleSoft}
                fillOpacity={0.7}
              />
              <Radar
                name={`${benchmark.name} · lower`}
                dataKey="__band_low"
                stroke="transparent"
                fill="white"
                fillOpacity={1}
              />
              <Radar
                name={`${benchmark.name} · median`}
                dataKey="__band_median"
                stroke={BRAND.purpleLight}
                strokeDasharray="4 4"
                fill="transparent"
              />
            </>
          ) : null}

          {series.map((s, i) => (
            <Radar
              key={s.name}
              name={s.name}
              dataKey={s.name}
              stroke={s.colour || COMPARE_PALETTE[i % COMPARE_PALETTE.length]}
              fill={s.colour || COMPARE_PALETTE[i % COMPARE_PALETTE.length]}
              fillOpacity={series.length > 1 ? 0.15 : 0.32}
              strokeWidth={2}
            />
          ))}
          <Tooltip
            contentStyle={{ fontSize: 12, border: "1px solid #ddd" }}
            formatter={(value, name) => (name && name.startsWith("__")) ? null : `${value}/${max}`}
          />
          {series.length > 1 ? (
            <Legend wrapperStyle={{ fontSize: 11, paddingTop: 10 }} />
          ) : null}
        </RadarChart>
      </ResponsiveContainer>
      {benchmark ? (
        <div className="text-[10.5px] text-zinc-500 mt-1 text-center">
          <span className="inline-block w-3 h-3 mr-1 align-middle" style={{ backgroundColor: BRAND.purpleSoft }} />
          {benchmark.name} ({benchmark.label}, n={benchmark.sampleSize}) — shaded area between low &amp; high; dashed line is the median
        </div>
      ) : null}
    </div>
  );
}
